// ─────────────────────────────────────────────────────────────────────────────
// Story Module — TypeScript interfaces matching Swagger spec
// Covers: GetStoryListing, GetActiveCategories, GetActiveFamilyList,
//         GetFamilyMemberList
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Story visibility / ownership type used in the listing filter.
 * null = All (no filter), 1 = Own, 2 = Shared, 3 = Tagged
 */
export enum StoryType {
  Own    = 1,
  Shared = 2,
  Tagged = 3,
}

// ── Listing request ───────────────────────────────────────────────────────────

/** POST body for /Story/GetStoryListing */
export interface StoryListingRequest {
  pageNumber:    number;
  pageSize:      number;
  searchText:    string;
  /** null = All Stories */
  categoryId:    number | null;
  /** null = All types (Own + Shared + Tagged) */
  storyType:     StoryType | null;
  /** null = All family groups */
  familySpaceId: string | null;
  sortColumn:    string | null;
  sortDirection: string | null;
}

// ── Listing response ──────────────────────────────────────────────────────────

/** A single story card returned inside the listing response. */
export interface StoryItem {
  storyId:      string;
  title:        string;
  thumbnailUrl: string;
  mediaUrl:     string;
  /** 0 = Audio, 1 = Video — kept for future use */
  mediaType:    number;
  mediaTypeName: string;
  /** Duration in seconds — format with formatDuration() */
  duration:     number;
  categoryName: string;
  categoryId:   number;
  familyName:   string;
  /** ISO date string — format with getRelativeTime() */
  createdOn:    string;
  viewCount:    number;
  likeCount:    number;
  /** true → story was created in response to a scheduled prompt */
  isFromPrompt: boolean;
  /** 1 = Own, 2 = Shared, 3 = Tagged */
  storySource:  number;
  totalRecords: number;
}

/** Top-level data shape for the GetStoryListing response. */
export interface StoryListingData {
  stories:      StoryItem[];
  totalRecords: number;
}

// ── Category ──────────────────────────────────────────────────────────────────

/** One item from GET /Story/GetActiveCategories */
export interface StoryCategory {
  categoryId:  number;
  categoryName: string;
  iconUrl:      string;
  totalPrompt:  number;
}

// ── Family space ──────────────────────────────────────────────────────────────

/** One item from GET /Story/GetActiveFamilyList */
export interface FamilySpace {
  familySpaceId: string;
  familyName:    string;
}

// ── Family member (for tagging / sharing) ─────────────────────────────────────

/** One item from GET /Story/GetFamilyMemberList */
export interface FamilyMember {
  familyMemberId: string;
  image:          string;
  fullName:       string;
  relationship:   string;
  familySpaceId:  string;
  familyName:     string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Story Detail — GET /Story/GetStoryDetail/{storyId}
// ─────────────────────────────────────────────────────────────────────────────

/** Reaction emoji constants — reactionType number → display emoji. */
export enum ReactionType {
  Heart = 0,
  Smile = 1,
  Wow   = 2,
  Sad   = 3,
}

/** A family member tagged inside a story. */
export interface TaggedPerson {
  familyMemberId: string;
  fullName:       string;
  profilePhoto:   string;
}

/** A single reaction entry returned with the story detail. */
export interface StoryReaction {
  reactionId:    string;
  userId:        string;
  /** Display name of the user who reacted. */
  userName:      string;
  /** Matches ReactionType enum (0=Heart, 1=Smile, 2=Wow, 3=Sad). */
  reactionType:  number;
  /** true when this reaction belongs to the currently logged-in user. */
  isCurrentUser: boolean;
  createdOn:     string;
}

/** A compact story card shown in the Related Stories section. */
export interface RelatedStory {
  storyId:      string;
  title:        string;
  thumbnailUrl: string;
  /** 0 = Audio, 1 = Video, 2 = Photo */
  mediaType:    number;
  /** Duration in seconds */
  duration:     number;
  createdOn:    string;
  viewCount:    number;
}

/**
 * Full story detail shape from GET /Story/GetStoryDetail/{storyId}.
 * Maps exactly to the Swagger response schema.
 */
export interface StoryDetail {
  storyId:                string;
  title:                  string;
  mediaUrl:               string;
  thumbnailUrl:           string;
  /** 0 = Audio, 1 = Video, 2 = Photo */
  mediaType:              number;
  mediaTypeName:          string;
  /** Duration in seconds — format with formatDuration(). */
  duration:               number;
  categoryName:           string;
  categoryId:             number;
  /** Display name of the user who uploaded the story. */
  uploadedBy:             string;
  uploadedByProfilePhoto: string;
  uploadedByUserId:       string;
  viewCount:              number;
  reactionCount:          number;
  /** ISO date string (date portion only, e.g. "2026-05-28"). */
  storyDate:              string;
  location:               string;
  emotionalTone:          string;
  transcriptVerbatim:     string;
  transcriptNarrative:    string;
  /** 0 = show Verbatim transcript, 1 = show Narrative transcript. */
  selectedTranscriptType: number;
  visibilityTypeId:       number;
  createdOn:              string;
  /** true when the story was uploaded by the currently logged-in user. */
  isOwnStory:             boolean;
  taggedPeople:           TaggedPerson[];
  reactions:              StoryReaction[];
  relatedStories:         RelatedStory[];
}

// ── Reaction requests ─────────────────────────────────────────────────────────

/** POST body for /Story/AddReaction */
export interface AddReactionRequest {
  storyId:      string;
  reactionType: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Story creation flow — request / response models
// ─────────────────────────────────────────────────────────────────────────────

/**
 * GET /Story/GetPromptsForCategory/{categoryId}
 * Returned item — used for both AI prompts and custom questions.
 * - AI prompt:     promptMasterId > 0, customQuestionId == null,  source == 'AI'
 * - Custom prompt: promptMasterId == 0, customQuestionId != null, source == 'Custom'
 */
export interface ActiveCategoryResponseModel {
  storyCategoryId: number;
  categoryId: number;
  categoryName: string;
  iconUrl?: string | null;
  categoryIconUrl?: string | null;
  totalPrompt?: number;
}

export interface PromptResponseModel {
  promptMasterId: number;
  promptText: string | null;
  source: string | null;           // 'AI' | 'Custom'
  customQuestionId: string | null; // UUID — present for custom questions
}

/** POST /Story/AddCustomQuestion */
export interface AddCustomQuestionRequest {
  categoryId: number;
  questionText: string;
}

/** PUT /Story/UpdateCustomQuestion */
export interface UpdateCustomQuestionRequest {
  customQuestionId: string; // UUID
  questionText: string;
}

/** GET /Story/GetEmotionalTones */
export interface EmotionalToneResponseModel {
  emotionalToneId: number;
  toneName: string | null;
}

/**
 * Question line-item inside CreateAndPublishStoryRequest.questions[]
 * questionSource: 1 = AI PromptMaster, 2 = Custom Question
 */
export interface StoryQuestionItem {
  questionId: string;      // UUID — for AI use promptIdToUuid(promptMasterId)
  questionSource: number;  // 1 | 2
  questionOrder: number;
  startTime?: number | null;
  endTime?: number | null;
}

/**
 * POST /Story/CreateAndPublishStory
 * MediaType:          1 = Video, 2 = Audio, 3 = Image+Audio
 * VisibilityTypeId:   1 = Private, 2 = Shared, 3 = Public, 5 = SelectedMembers
 * SelectedTranscriptType: 1 = Verbatim, 2 = Narrative
 */
export interface CreateAndPublishStoryRequest {
  categoryId: number;
  mediaType: number;
  questions?: StoryQuestionItem[];
  title: string;
  mediaFileName?: string | null;
  duration?: number | null;
  storyDate?: string | null;   // "YYYY-MM-DD"
  location?: string | null;
  emotionalToneId?: number | null;
  visibilityTypeId: number;
  transcriptVerbatim?: string | null;
  transcriptNarrative?: string | null;
  selectedTranscriptType: number;
  trimStart?: number | null;
  trimEnd?: number | null;
  taggedFamilyMemberIds?: string[] | null;
  sharedWithUserIds?: string[] | null;
}

/** POST /Story/CreateAndPublishStory — response */
export interface PublishStoryResponse {
  storyId: string;
  message: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// UI-only helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Augmented prompt item used in the Step 3 question list UI */
export interface UiPromptItem {
  /** Unique key (e.g. "ai-3" | "custom-uuid") */
  key: string;
  promptMasterId: number;
  customQuestionId: string | null;
  questionText: string;
  source: 'AI' | 'Custom';
  selected: boolean;
  editing: boolean;
  editDraft: string;
}

/**
 * Convert int promptMasterId to a UUID-like string so it satisfies the
 * StoryQuestionItem.questionId UUID format when questionSource = 1 (AI).
 */
export function promptIdToUuid(id: number): string {
  return `00000000-0000-0000-0000-${id.toString().padStart(12, '0')}`;
}

